/**
 * Placing a report's crosshair with a finger, and the loupe that shows where
 * it lands (web and app use these; neither has a formula of its own).
 *
 * One finger: a tap places the cross where it lands, a drag pans, and a
 * finger held still for LONG_PRESS_MS brings up the loupe — a finger hides
 * exactly the spot it touches, so the loupe above it shows that spot
 * magnified with the crosshair in the middle; moving the finger moves the
 * cross, lifting it places the report there. Two fingers zoom and pan.
 * Owner, 06.10.2026: „Intuitiv möchte ich doch bei der Schadenserfassung mit
 * einem Finger in alle Richtungen verschieben.“
 */
import { boardPointToNormalized, containRect, type NormalizedPoint, type Size } from '@fsv/core';
import { isTap, screenToBoard, type BoxSize, type Point, type Transform } from './zoom';

/** Diameter of the loupe on screen. */
export const LOUPE_SIZE = 120;
/** Magnification inside the loupe, on top of the board's current zoom… */
export const LOUPE_ZOOM = 2.5;
/** …but never more than this in total: past it a raster drawing is only blur. */
export const LOUPE_MAX_TOTAL = 8;
/** Gap between the fingertip and the loupe's edge; the finger pad is about this tall. */
export const LOUPE_LIFT = 36;

/**
 * The stored point under a finger. A finger dragged past the drawing's edge
 * (into the letterbox, or off the board) keeps the cross on that edge instead
 * of dropping it — the person is still placing, not cancelling.
 */
export function placeAt(t: Transform, finger: Point, image: Size, board: BoxSize): NormalizedPoint {
  const rect = containRect(image, board);
  const b = screenToBoard(t, finger);
  const x = Math.min(rect.left + rect.width, Math.max(rect.left, b.x));
  const y = Math.min(rect.top + rect.height, Math.max(rect.top, b.y));
  // Clamped to the rect, so this is never null; the fallback only satisfies the type.
  return boardPointToNormalized(x, y, image, board) ?? { x: 0, y: 0 };
}

/**
 * Where the loupe sits: centred on the finger horizontally, above it with
 * LOUPE_LIFT to spare; below the finger when the board's top edge leaves no
 * room above. Kept inside the board horizontally, so it is never cut off.
 */
export function loupeFrame(finger: Point, board: BoxSize, size = LOUPE_SIZE, lift = LOUPE_LIFT): { left: number; top: number } {
  const left = Math.min(Math.max(finger.x - size / 2, 0), Math.max(board.width - size, 0));
  const above = finger.y - lift - size;
  return { left, top: above >= 0 ? above : finger.y + lift };
}

/**
 * The transform of the board-sized picture layer inside the loupe, so that
 * `target` (a point on the board, in board coordinates) is in the loupe's
 * centre, magnified LOUPE_ZOOM times beyond the board's current zoom.
 */
export function loupeTransform(t: Transform, target: Point, size = LOUPE_SIZE, zoom = LOUPE_ZOOM): Transform {
  const scale = Math.min(t.scale * zoom, LOUPE_MAX_TOTAL);
  return { scale, x: size / 2 - target.x * scale, y: size / 2 - target.y * scale };
}

/** How long a finger must stay put before the loupe comes up. */
export const LONG_PRESS_MS = 350;

/**
 * What one finger on the board is doing: `pending` until it either moves past
 * the tap slop (`pan`) or is held for LONG_PRESS_MS (`loupe`). Neither switch
 * goes back — a pan never turns into a loupe, and with the loupe up a move
 * moves the cross, not the drawing.
 */
export type PressMode = 'pending' | 'pan' | 'loupe';

export function pressAfterMove(mode: PressMode, down: Point, at: Point): PressMode {
  return mode === 'pending' && !isTap(down, at) ? 'pan' : mode;
}

/** The hold timer ran out; only a finger that has not started panning gets the loupe. */
export function pressAfterHold(mode: PressMode): PressMode {
  return mode === 'pending' ? 'loupe' : mode;
}

/**
 * What lifting the finger does: a finger that never moved nor waited is a tap
 * (place where it lifted), one with the loupe places the cross it showed, a
 * pan places nothing.
 */
export function pressOnRelease(mode: PressMode): 'tap' | 'loupe' | 'none' {
  return mode === 'pending' ? 'tap' : mode === 'loupe' ? 'loupe' : 'none';
}
